import React, { useRef, useLayoutEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Server, Zap, Radio } from 'lucide-react'
import { Modal } from './Modal'
import { getSubscriptions } from '../api/buzz'
import type { BuzzEvent, Subscription } from '../types'

interface SVGLine { x1: number; y1: number; x2: number; y2: number; arrow?: boolean }

function fmtDt(d: string) {
  return new Date(d).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

function fmtTTL(ms: number) {
  if (ms < 1000) return `${ms}ms`
  if (ms < 60_000) return `${ms / 1000}s`
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m`
  return `${Math.round(ms / 3_600_000)}h`
}

type NodeColor = 'slate' | 'blue' | 'emerald'

const nodeStyles: Record<NodeColor, { card: string; label: string; icon: string; title: string }> = {
  slate:   { card: 'bg-paper-sunken border-paper-border', label: 'text-ink-dim',   icon: 'text-ink-dim',   title: 'text-ink'  },
  blue:    { card: 'bg-accent-soft border-accent/20',      label: 'text-accent/70', icon: 'text-accent',    title: 'text-ink'  },
  emerald: { card: 'bg-emerald-50 border-emerald-200', label: 'text-emerald-500', icon: 'text-emerald-600', title: 'text-emerald-900' },
}

interface NodeProps {
  color: NodeColor
  label: string
  icon: React.ReactNode
  title: string
  subtitle?: string
  meta?: string
}

const FlowNode = React.forwardRef<HTMLDivElement, NodeProps>(
  ({ color, label, icon, title, subtitle, meta }, ref) => {
    const s = nodeStyles[color]
    return (
      <div ref={ref} className={`w-44 rounded-lg border px-4 py-3 flex-shrink-0 ${s.card}`}>
        <div className={`flex items-center gap-1.5 mb-2`}>
          <span className={s.icon}>{icon}</span>
          <span className={`text-[10px] font-semibold uppercase tracking-widest ${s.label}`}>{label}</span>
        </div>
        <p className={`text-sm font-semibold truncate ${s.title}`}>{title}</p>
        {subtitle && <p className="text-xs text-ink-dim truncate mt-0.5">{subtitle}</p>}
        {meta && <p className="text-theme-xs text-ink-dim mt-1.5">{meta}</p>}
      </div>
    )
  }
)
FlowNode.displayName = 'FlowNode'

interface Props {
  event: BuzzEvent
  onClose: () => void
}

export function EventFlow({ event, onClose }: Props) {
  const { data: subs = [], isLoading } = useQuery({
    queryKey: ['event-flow-subs', event.title],
    queryFn: () => getSubscriptions(event.title).then((r) => r.data),
  })

  const containerRef  = useRef<HTMLDivElement>(null)
  const producerRef   = useRef<HTMLDivElement>(null)
  const buzzRef       = useRef<HTMLDivElement>(null)
  const consumerRefs  = useRef<(HTMLDivElement | null)[]>([])
  const [lines,   setLines]   = useState<SVGLine[]>([])
  const [svgDim,  setSvgDim]  = useState({ w: 0, h: 0 })

  // Track previous serialized values to avoid setState loops driven by ResizeObserver:
  // RO fires → setState → re-render → DOM change → RO fires → ...
  const prevDiagramKey = useRef('')

  useLayoutEffect(() => {
    const r1 = (n: number) => Math.round(n * 2) / 2  // round to 0.5px

    const measure = () => {
      const cEl = containerRef.current
      const pEl = producerRef.current
      const bEl = buzzRef.current
      if (!cEl || !pEl || !bEl) return

      const cRect = cEl.getBoundingClientRect()
      const rel = (el: HTMLElement) => {
        const r = el.getBoundingClientRect()
        return {
          left:    r1(r.left   - cRect.left),
          right:   r1(r.right  - cRect.left),
          centerY: r1((r.top + r.bottom) / 2 - cRect.top),
        }
      }

      const p = rel(pEl)
      const b = rel(bEl)
      const newLines: SVGLine[] = []

      // Producer → Buzz
      newLines.push({ x1: p.right, y1: p.centerY, x2: b.left, y2: b.centerY, arrow: true })

      const consumerPts = consumerRefs.current
        .filter((r): r is HTMLDivElement => r !== null)
        .map((r) => { const rr = rel(r); return { x: rr.left, y: rr.centerY } })

      if (consumerPts.length === 1) {
        newLines.push({ x1: b.right, y1: b.centerY, x2: consumerPts[0].x, y2: consumerPts[0].y, arrow: true })
      } else if (consumerPts.length > 1) {
        const trunkX    = r1(b.right + (consumerPts[0].x - b.right) / 2)
        const trunkTopY = Math.min(b.centerY, consumerPts[0].y)
        const trunkBotY = Math.max(b.centerY, consumerPts[consumerPts.length - 1].y)

        newLines.push({ x1: b.right, y1: b.centerY, x2: trunkX, y2: b.centerY })
        newLines.push({ x1: trunkX, y1: trunkTopY, x2: trunkX, y2: trunkBotY })
        consumerPts.forEach((c) => {
          newLines.push({ x1: trunkX, y1: c.y, x2: c.x, y2: c.y, arrow: true })
        })
      }

      const newW = r1(cRect.width)
      const newH = r1(cRect.height)
      const key  = JSON.stringify({ w: newW, h: newH, lines: newLines })

      // Only update state when something actually changed — prevents ResizeObserver loops
      if (key === prevDiagramKey.current) return
      prevDiagramKey.current = key

      setLines(newLines)
      setSvgDim({ w: newW, h: newH })
    }

    measure()
    const ro = new ResizeObserver(measure)
    if (containerRef.current) ro.observe(containerRef.current)
    return () => ro.disconnect()
  }, [subs, isLoading])

  return (
    <Modal title={event.title} onClose={onClose} size="lg">
      <div ref={containerRef} className="relative py-12 px-2 min-h-[160px]">
        {svgDim.w > 0 && (
          <svg
            className="absolute inset-0 pointer-events-none"
            width={svgDim.w}
            height={svgDim.h}
            style={{ overflow: 'visible' }}
          >
            <defs>
              <marker id="ev-arrow" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
                <path d="M0,0 L7,3.5 L0,7 Z" fill="#d0d5dd" />
              </marker>
            </defs>
            {lines.map((l, i) => (
              <line
                key={i}
                x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2}
                stroke="#d0d5dd"
                strokeWidth={1.5}
                markerEnd={l.arrow ? 'url(#ev-arrow)' : undefined}
              />
            ))}
          </svg>
        )}

        <div className="flex items-center justify-between gap-6">
          {/* Producer */}
          <FlowNode
            ref={producerRef}
            color="slate"
            label="Producer"
            icon={<Server size={14} />}
            title={event.producer}
            meta={fmtDt(event.createdAt)}
          />

          {/* Buzz hub */}
          <FlowNode
            ref={buzzRef}
            color="blue"
            label="Buzz"
            icon={<Zap size={14} />}
            title={event.title}
            subtitle={event.description || undefined}
            meta={`TTL ${fmtTTL(event.lifeDurationMs)} · ${event.isExpired ? 'Expired' : 'Active'}`}
          />

          {/* Consumers */}
          {isLoading ? (
            <div className="w-44 text-sm text-ink-dim text-center">Loading…</div>
          ) : subs.length === 0 ? (
            <div className="w-44 flex items-center justify-center rounded-lg border border-dashed border-paper-border px-4 py-5">
              <p className="text-xs text-ink-dim text-center">No subscribers</p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {(subs as Subscription[]).map((sub, i) => (
                <FlowNode
                  key={sub.id}
                  ref={(el) => { consumerRefs.current[i] = el }}
                  color="emerald"
                  label="Consumer"
                  icon={<Radio size={14} />}
                  title={sub.subscriberId}
                  subtitle={sub.callbackUrl}
                  meta={`since ${fmtDt(sub.createdAt)}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {event.metadata && Object.keys(event.metadata).length > 0 && (
        <div className="px-2 pb-2">
          <p className="text-theme-xs font-semibold uppercase tracking-widest text-ink-dim mb-1.5">Metadata</p>
          <pre className="bg-paper-sunken border border-paper-border rounded-md px-4 py-3 text-xs font-mono text-ink overflow-x-auto max-h-64 scrollbar-thin">
            {JSON.stringify(event.metadata, null, 2)}
          </pre>
        </div>
      )}
    </Modal>
  )
}
